import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { LogOut, Settings } from "lucide-react"
import { notify } from "@utils/notify"
import { useSelector, useDispatch } from "react-redux"
import { magic } from "@utils/Magic"
import logoutUser from "@services/auth/logoutUser"

const ProfileMenu = ({ position = undefined }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { user, getUserByIdLoading } = useSelector((state) => state.user)

  const displayName = user ? `${user.firstName} ${user.lastName}` : "Loading..."

  const initials = user
    ? displayName
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
    : getUserByIdLoading ? "..." : "U"

  const confirmLogout = async () => {
    try {
      // console.log("Attempting to logout user ID:", user.id);
      await logoutUser(user._id);

      window.dispatchEvent(new Event('userStateChange'));

      localStorage.clear();
      // Clear sessionStorage
      sessionStorage.clear();

      // Clear Redux store
      dispatch({ type: 'RESET_STORE' }); // You'll need to add this action to your Redux store

      await magic.user.logout();

      // Clear any cookies
      document.cookie.split(";").forEach(cookie => {
        const [name] = cookie.split("=");
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });

      notify("Logged out successfully", "success");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      notify("Failed to logout. Please try again.", "error");
    }
  }
  // console.log("userid", user)

  const handleSettingsClick = () => {
    navigate("/settings")
    setIsOpen(false)
  }

  const handleLogout = () => {
    setShowLogoutConfirm(true)
  }

  return (
    <div className="relative">
      <div className="relative z-50">
        <button onClick={() => setIsOpen(!isOpen)}>
          <div className="flex items-center justify-center w-10 h-10 text-white bg-indigo-600 rounded-full">
            {initials}
          </div>
        </button>
      </div>

      {/* Dropdown and backdrop */}
      {isOpen && (
        <>
          {/* Light blur overlay */}
          <div
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-40"
          />

          {/* Dropdown modal */}
          <div className={`absolute ${position === 'sidebar' ? 'left-[105px] -translate-x-1/2 bottom-full mb-2' : 'right-0 mt-2'} w-64 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 text-sm`}>
            {/* User Info with hover effect */}
            <div className="p-4 transition-colors border-b border-gray-200 cursor-default bg-gray-50 hover:bg-gray-100">
              <p className="font-semibold text-gray-800 truncate">{getUserByIdLoading ? "Loading..." : displayName}</p>
              <p className="text-gray-500 truncate">{getUserByIdLoading ? "Loading..." : (user?.email ?? "No email")}</p>
            </div>

            {/* Settings Button */}
            <button
              onClick={handleSettingsClick}
              className="flex items-center w-full gap-2 px-4 py-3 text-left transition-colors border-b border-gray-200 hover:bg-gray-100 focus:outline-none"
            >
              <Settings size={16} />
              <span className="block text-gray-700">Settings</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-3 text-left transition-colors hover:bg-red-50 focus:outline-none"
            >
              <div className="flex items-center gap-2 text-red-600">
                <LogOut size={16} />
                <span>Logout</span>
              </div>
            </button>

          </div>
        </>
      )}

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm p-6 mx-4 bg-white rounded-lg">
            <h3 className="mb-4 text-lg font-medium text-gray-900">
              Confirm Logout
            </h3>
            <p className="mb-6 text-gray-500">
              Are you sure you want to logout?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfileMenu
